import React from 'react';

import { Grid, GridProps } from '@mui/material';

import { useNotificationsContext } from '../../../contexts/NotificationsContext';
import { useReservationLimitInPeriodContext } from '../../../contexts/ReservationsLimitInPeriodContext';
import { VennueAccessRequestItem, VenueAccessREnum } from '../../../graphql/API';
import useSubscriptionAccess from '../../../hooks/useSubscriptionAccess';
import ProfileCardSkeleton from '../../../pages/Collabs/components/ProfileCardSkeleton';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import {
  fetchAccountBalance,
  handleBalanceIsLoading,
} from '../../../redux/features/balance/balanceSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { delay } from '../../../utils/delay';
import { brandPromotionLimit } from '../../../utils/reservations';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { AccessRequestAlert } from '../../Alerts/AccessRequestAlert';
import {
  AccessRequestAcceptedDialog,
  accessRequestPopupStorageKey,
} from '../../Dialogs/AccessRequestAccepted/AccessRequestAcceptedDialog';
import { AccessRequestConfirmationIncentiveDialog } from '../../Dialogs/AccessRequestConfirmationIncentive/AccessRequestConfirmationIncentiveDialog';
import {
  AccessRequestDeclineConfirmationDialog,
  accessRequestDeclineConfirmationStorageKey,
} from '../../Dialogs/AccessRequestDeclineConfirmation/AccessRequestDeclineConfirmationDialog';
import { ProductRequestsRejectedDialog } from '../../Dialogs/ProductRequestsRejected/ProductRequestsRejectedDialog';
import { useAction } from '../../Tables/AccessRequest/useAction';
import { mapToActionRequest } from '../../Tables/AccessRequest/utils';
import { AccessRequestUserProfileCard } from '../components/AccessRequestUserProfileCard';
import { NoPendingAccessRequest } from '../components/NoPendingAccessRequest';

type Props = { data: VennueAccessRequestItem[]; isLoading: boolean, isPromotionLimitHit?: boolean };

const commonGrid = (key: string): GridProps => ({
  item: true,
  key,
  xs: 2.4,
  md: 3,
  lg: 3,
  xl: 2.4,
});

export const AccessRequestGrid: React.FC<Props> = ({ data, isLoading, isPromotionLimitHit }) => {
  const subscriptionAccess = useSubscriptionAccess();
  const actionProps = useAction();

  const appDispatch = useAppDispatch();

  const { refetchReservationsHandler } = useNotificationsContext() || {};
  const [selectedAccessRequest, setSelectedAccessRequest] =
    React.useState<VennueAccessRequestItem | null>(null);
  const [dontShowAccessRequestPopup, setDontShowAccessRequestPopup] = React.useState(false);

  const [showProductRejectDialog, setShowProductRejectDialog] = React.useState(false);
  const [selectedRejectProductRequest, setSelectedRejectProductRequest] =
    React.useState<VennueAccessRequestItem | null>(null);

  const [incentiveDialog, setIncentiveDialog] = React.useState(false);

  const [showDeclineConfirmationDialog, setShowDeclineConfirmationDialog] = React.useState(false);
  const [selectedDeclineAccessRequest, setSelectedDeclineAccessRequest] =
    React.useState<VennueAccessRequestItem | null>(null);

  const venueState = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const { totalWaitingAndShippedReservations, totalReservedAndCheckedInReservations } =
    useReservationLimitInPeriodContext() || {};

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const handleRefetchVenueBalance = React.useCallback(() => {
    appDispatch(handleBalanceIsLoading());
    delay(5000).then(() => {
      appDispatch(fetchAccountBalance({ venueID: venueState?.id || '' }));
    });
  }, [appDispatch, venueState?.id]);

  React.useEffect(() => {
    if (actionProps.successState && venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      if (refetchReservationsHandler) refetchReservationsHandler();
    }
  }, [venueState?.tags, actionProps.successState, refetchReservationsHandler]);

  React.useEffect(() => {
    if (!!selectedAccessRequest?.offer?.incentiveValue && actionProps.successState) {
      handleRefetchVenueBalance();
    }
  }, [actionProps.successState, selectedAccessRequest]);

  const handleAccessRequestSuccessPopup = () => {
    const showAccessRequestPopupValue = localStorage.getItem(accessRequestPopupStorageKey);
    const shouldShow = showAccessRequestPopupValue
      ? JSON.parse(showAccessRequestPopupValue)
      : false;
    setDontShowAccessRequestPopup(shouldShow);
  };

  const handleSelectedAccessRequest = (accessRequest: VennueAccessRequestItem | null) => {
    setSelectedAccessRequest(accessRequest);
    handleAccessRequestSuccessPopup();
  };

  const handleSelectedRejectProductRequest = (accessRequest: VennueAccessRequestItem | null) => {
    setSelectedRejectProductRequest(accessRequest);
    handleRejectProductRequestDialog();
  };

  const handleRejectProductRequestDialog = () => {
    setShowProductRejectDialog((prev) => !prev);
  };

  const handleProceedWithReject = React.useCallback(
    (accessRequest: VennueAccessRequestItem | null) => {
      if (isOnlineBrand && accessRequest && (accessRequest.totalRequested || 0) >= 2) {
        setSelectedRejectProductRequest(accessRequest);
        setShowProductRejectDialog(true);
      } else {
        actionProps.onAction(
          mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined),
        );
      }
    },
    [isOnlineBrand, actionProps],
  );

  const handleRejectWithConfirmation = React.useCallback(
    (accessRequest: VennueAccessRequestItem | null) => {
      const dontShowValue = localStorage.getItem(accessRequestDeclineConfirmationStorageKey + String(venueState?.id));
      const shouldSkipConfirmation = dontShowValue ? JSON.parse(dontShowValue) : false;

      if (shouldSkipConfirmation) {
        handleProceedWithReject(accessRequest);
      } else {
        setSelectedDeclineAccessRequest(accessRequest);
        setShowDeclineConfirmationDialog(true);
      }
    },
    [handleProceedWithReject],
  );

  const handleDeclineConfirmation = React.useCallback(
    (accessRequest: VennueAccessRequestItem | null) => {
      setShowDeclineConfirmationDialog(false);
      setSelectedDeclineAccessRequest(null);
      handleProceedWithReject(accessRequest);
    },
    [handleProceedWithReject],
  );

  const handleCloseDeclineConfirmationDialog = () => {
    setShowDeclineConfirmationDialog(false);
    setSelectedDeclineAccessRequest(null);
  };

  React.useEffect(() => {
    handleAccessRequestSuccessPopup();
  }, []);

  const handleCloseAccessRequestPopup = () => {
    setSelectedAccessRequest(null);
  };

  const handleIncentiveDialog = () => {
    setIncentiveDialog((prev) => !prev);
  };

  const onAcceptIncentiveHandle = (accessRequest: VennueAccessRequestItem | null) => {
    setSelectedAccessRequest(accessRequest);
    handleIncentiveDialog();
  };

  const onIncentiveConfirmHandle = () => {
    if (selectedAccessRequest) {
      handleIncentiveDialog();
      actionProps.onAction(
        mapToActionRequest(selectedAccessRequest ?? undefined, VenueAccessREnum.accepted),
      );
      handleAccessRequestSuccessPopup();
    }
  };

  const totalReservations = React.useMemo(
    () =>
      (isOnlineBrand
        ? totalWaitingAndShippedReservations
        : totalReservedAndCheckedInReservations) || 0,
    [isOnlineBrand, totalWaitingAndShippedReservations, totalReservedAndCheckedInReservations],
  );

  return (
    <>
      <Grid container spacing={3} rowSpacing={10}>
        {!isLoading
          ? data.map((x, index) => (
              <Grid {...commonGrid(`${x.offerID}`)} key={index}>
                <AccessRequestUserProfileCard
                  accessRequest={x}
                  fullProfile={
                    (subscriptionAccess?.includes('COMPLETE_USER_PROFILES') &&
                      !isPromotionLimitHit) ||
                    false
                  }
                  isPromotionLimitHit={isPromotionLimitHit}
                  onAccept={handleSelectedAccessRequest}
                  isOnlineBrand={venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)}
                  onReject={handleSelectedRejectProductRequest}
                  onRejectWithConfirmation={handleRejectWithConfirmation}
                  onAcceptIncentive={onAcceptIncentiveHandle}
                  {...actionProps}
                />
              </Grid>
            ))
          : [...Array(6)].map((x, i) => (
              <Grid {...commonGrid(`grid-skeleton-${i}`)}>
                <ProfileCardSkeleton />
              </Grid>
            ))}
        <Grid item xs={12}>
          {!isLoading && data.length === 0 && (
            <NoPendingAccessRequest isOnlineBrand={isOnlineBrand} />
          )}
        </Grid>
      </Grid>
      <AccessRequestAlert
        open={
          ((selectedAccessRequest || false) &&
            actionProps.successState &&
            isOnlineBrand &&
            dontShowAccessRequestPopup) ||
          false
        }
        onClose={() => actionProps.setSuccessState(false)}
        accessRequest={selectedAccessRequest}
      />

      {selectedAccessRequest && !dontShowAccessRequestPopup && (
        <AccessRequestAcceptedDialog
          open={
            ((selectedAccessRequest || false) &&
              actionProps.successState &&
              isOnlineBrand &&
              !dontShowAccessRequestPopup) ||
            false
          }
          accessRequest={selectedAccessRequest}
          onClose={handleCloseAccessRequestPopup}
          reservationID={actionProps.reservationID}
        />
      )}

      <ProductRequestsRejectedDialog
        open={showProductRejectDialog}
        accessRequest={selectedRejectProductRequest}
        onClose={handleRejectProductRequestDialog}
      />

      <AccessRequestConfirmationIncentiveDialog
        open={incentiveDialog}
        onClose={handleIncentiveDialog}
        accessRequest={selectedAccessRequest}
        onConfirm={onIncentiveConfirmHandle}
      />

      <AccessRequestDeclineConfirmationDialog
        open={showDeclineConfirmationDialog}
        onClose={handleCloseDeclineConfirmationDialog}
        accessRequest={selectedDeclineAccessRequest}
        onDecline={handleDeclineConfirmation}
        venueID={String(venueState?.id)}
      />
    </>
  );
};
